import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  FolderIcon,
} from '@heroicons/react/24/outline'
import {
  SkillApi,
  SkillCatalogItem,
  SkillCatalogItemType,
  SkillLibraryAccess,
  SkillLibraryUnlockedCatalog,
  SkillTreeNode,
  catalogTypeLabel,
  formatFileSize,
  formatGroupTitle,
  formatSkillDate,
  groupCatalogKey,
  isFileCatalogType,
  skillFolderBasename,
} from '../../services/skillApi'
import SkillTree from '../../components/skills/SkillTree'
import SkillFileViewer from '../../components/skills/SkillFileViewer'
import Ai4baEnrollBanner from '../../components/skills/Ai4baEnrollBanner'
import { useAuthStore } from '../../store/authStore'
import { findCatalogBySkillKey } from '../../utils/skillLibraryLinks'

const COURSE_CODE_LABEL: Record<string, string> = {
  BAAIONL: 'BA',
  AICODING: 'AI Coding',
  AI4BA: 'AI4BA',
}

const SkillLibraryPage = () => {
  const { catalogId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [items, setItems] = useState<SkillCatalogItem[]>([])
  const [libraryAccess, setLibraryAccess] = useState<SkillLibraryAccess | null>(null)
  const [filter, setFilter] = useState<'ALL' | SkillCatalogItemType>('ALL')
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(true)
  const [item, setItem] = useState<SkillCatalogItem | null>(null)
  const [viewCourseId, setViewCourseId] = useState<number | null>(null)
  const [tree, setTree] = useState<SkillTreeNode[]>([])
  const [selectedFile, setSelectedFile] = useState<SkillTreeNode | null>(null)
  const [downloading, setDownloading] = useState(false)
  const skillDeepLinkHandled = useRef(false)

  const skillQuery = (searchParams.get('skill') || '').trim()
  const courseIdQuery = searchParams.get('courseId')
  const courseIdFromQuery =
    courseIdQuery && /^\d+$/.test(courseIdQuery) ? Number(courseIdQuery) : null
  const numericId =
    catalogId && /^\d+$/.test(catalogId) ? Number(catalogId) : null
  const slugParam =
    catalogId && !/^\d+$/.test(catalogId) ? decodeURIComponent(catalogId) : null

  const unlockByCatalogId = useMemo(() => {
    const map = new Map<number, SkillLibraryUnlockedCatalog>()
    for (const u of libraryAccess?.unlockedCatalog || []) {
      map.set(u.catalogId, u)
    }
    return map
  }, [libraryAccess])

  const activeCourseId = viewCourseId ?? courseIdFromQuery
  const courseOpts = activeCourseId ? { courseId: activeCourseId } : undefined

  const canViewCatalog = (c: SkillCatalogItem) =>
    c.isPublic || unlockByCatalogId.has(c.id)

  const catalogViewPath = (c: SkillCatalogItem) => {
    const unlock = unlockByCatalogId.get(c.id)
    if (!c.isPublic && unlock) {
      return `/skills/${c.id}?courseId=${unlock.courseId}`
    }
    return `/skills/${c.id}`
  }

  const requireLoginToDownload = () => {
    toast.info('Đăng nhập để tải về')
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    navigate(`/login?returnUrl=${returnUrl}`)
  }

  const refreshList = async () => {
    const data = await SkillApi.listCatalog()
    setItems(data)
    return data
  }

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        await refreshList()
      } catch (e: any) {
        toast.error(e?.message || 'Không tải được thư viện')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setLibraryAccess(null)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const data = await SkillApi.getLibraryAccess()
        if (!cancelled) setLibraryAccess(data)
      } catch {
        if (!cancelled) {
          setLibraryAccess({
            eligible: false,
            courses: [],
            links: [],
            unlockedCatalog: [],
          })
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  // Deep-link ?skill=ba-xxx → mở detail nếu public/unlocked, không thì prefill tìm kiếm
  useEffect(() => {
    if (!skillQuery || items.length === 0 || skillDeepLinkHandled.current) return
    if (numericId || slugParam) return
    // Wait for unlock map when authenticated so private unlocks resolve correctly
    if (isAuthenticated && libraryAccess === null) return

    skillDeepLinkHandled.current = true
    const hit = findCatalogBySkillKey(items, skillQuery)
    if (hit && canViewCatalog(hit)) {
      navigate(catalogViewPath(hit), { replace: true })
      return
    }
    setKeyword(skillQuery)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('skill')
        return next
      },
      { replace: true }
    )
    if (hit && !canViewCatalog(hit)) {
      toast.info('Skill này chưa công khai — chỉ xem được tên/mô tả trên danh sách')
    }
  }, [
    skillQuery,
    items,
    numericId,
    slugParam,
    navigate,
    setSearchParams,
    isAuthenticated,
    libraryAccess,
    unlockByCatalogId,
  ])

  // /skills/:slug (không phải số) → resolve sang id
  useEffect(() => {
    if (!slugParam || items.length === 0) return
    if (isAuthenticated && libraryAccess === null) return

    const hit = findCatalogBySkillKey(items, slugParam)
    if (hit && canViewCatalog(hit)) {
      navigate(catalogViewPath(hit), { replace: true })
    } else {
      toast.error('Không tìm thấy skill có thể xem với mã này')
      navigate('/skills', { replace: true })
    }
  }, [slugParam, items, navigate, isAuthenticated, libraryAccess, unlockByCatalogId])

  useEffect(() => {
    if (!numericId || Number.isNaN(numericId)) {
      setItem(null)
      setTree([])
      setSelectedFile(null)
      setViewCourseId(null)
      return
    }

    const load = async () => {
      try {
        setLoading(true)
        const data = await SkillApi.getCatalogTree(numericId)
        setItem(data.item)
        setTree(data.tree || [])
        // Mục là một file đơn (tài liệu / file cài đặt) → chọn sẵn để xem/tải ngay.
        const onlyRoot = data.tree?.length === 1 ? data.tree[0] : null
        setSelectedFile(onlyRoot && onlyRoot.nodeType === 'FILE' ? onlyRoot : null)
        setViewCourseId(
          data.unlockCourseId ??
            courseIdFromQuery ??
            unlockByCatalogId.get(numericId)?.courseId ??
            null
        )
      } catch (e: any) {
        toast.error(e?.message || 'Không mở được skill')
        setItem(null)
        setTree([])
        setViewCourseId(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [numericId, courseIdFromQuery, unlockByCatalogId])

  const handleDownloadFolder = async (node: SkillTreeNode) => {
    if (!isAuthenticated) {
      requireLoginToDownload()
      return
    }
    try {
      setDownloading(true)
      const folderName = skillFolderBasename(node.relativePath, node.name)
      await SkillApi.downloadFolder(node.id, folderName, courseOpts)
      toast.success(`Đã tải thư mục ${folderName}`)
    } catch (e: any) {
      toast.error(e?.message || 'Tải thư mục thất bại')
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadRoot = async () => {
    if (!item) return
    if (!isAuthenticated) {
      requireLoginToDownload()
      return
    }
    try {
      setDownloading(true)
      // Tên ZIP = tên thư mục skill trên disk (tree root / slug), không dùng tên hiển thị.
      const rootNode =
        tree.find((n) => n.id === item.rootNodeId) ||
        tree.find((n) => !n.parentId) ||
        null
      if (rootNode?.nodeType === 'FILE') {
        await SkillApi.downloadFile(rootNode.id, rootNode.name, courseOpts)
        toast.success(`Đã tải ${rootNode.name}`)
        return
      }
      const folderName =
        skillFolderBasename(rootNode?.relativePath, rootNode?.name) ||
        item.slug ||
        item.name
      await SkillApi.downloadFolder(item.rootNodeId, folderName, courseOpts)
      toast.success(`Đã tải skill ${folderName}`)
    } catch (e: any) {
      toast.error(e?.message || 'Tải thất bại')
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadFile = async (node: SkillTreeNode) => {
    if (!isAuthenticated) {
      requireLoginToDownload()
      return
    }
    try {
      setDownloading(true)
      await SkillApi.downloadFile(node.id, node.name, courseOpts)
      toast.success(`Đã tải ${node.name}`)
    } catch (e: any) {
      toast.error(e?.message || 'Tải file thất bại')
    } finally {
      setDownloading(false)
    }
  }

  /** Tải nhanh từ thẻ: mục là một file → tải thẳng file, là thư mục → ZIP. */
  const handleQuickDownload = async (c: SkillCatalogItem) => {
    if (!isAuthenticated) {
      requireLoginToDownload()
      return
    }
    const unlock = unlockByCatalogId.get(c.id)
    const opts = !c.isPublic && unlock ? { courseId: unlock.courseId } : undefined
    try {
      setDownloading(true)
      if (c.rootNodeType === 'FILE') {
        await SkillApi.downloadFile(c.rootNodeId, c.name, opts)
      } else {
        await SkillApi.downloadFolder(c.rootNodeId, c.slug || c.name, opts)
      }
      toast.success(`Đã tải ${c.name}`)
    } catch (e: any) {
      toast.error(e?.message || 'Tải thất bại')
    } finally {
      setDownloading(false)
    }
  }

  const filtered = items.filter((i) => {
    if (filter !== 'ALL' && i.itemType !== filter) return false
    const q = keyword.trim().toLowerCase()
    if (!q) return true
    const haystack = [i.name, i.description, i.groupPath, i.slug]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })

  const emptyMessage = (() => {
    if (keyword.trim()) {
      return `Không tìm thấy kết quả cho “${keyword.trim()}”`
    }
    switch (filter) {
      case 'WORKFLOW':
        return 'Chưa có workflow nào trong thư viện'
      case 'ARTIFACT':
        return 'Chưa có artifact nào trong thư viện'
      case 'SKILL':
        return 'Chưa có skill nào trong thư viện'
      case 'DOCUMENT':
        return 'Chưa có tài liệu nào trong thư viện'
      case 'INSTALLER':
        return 'Chưa có file cài đặt nào trong thư viện'
      default:
        return 'Thư viện đang trống'
    }
  })()

  /** Nhóm theo thư mục gom upload; section "Công khai" đứng đầu khi xem Tất cả (không keyword). */
  const groupedSections = (() => {
    const showPublicSection = filter === 'ALL' && !keyword.trim()
    const sections: { key: string; title: string; items: SkillCatalogItem[]; isPublicSection?: boolean }[] =
      []

    if (showPublicSection) {
      const pubs = filtered.filter((i) => i.isPublic)
      if (pubs.length > 0) {
        sections.push({
          key: '__public__',
          title: 'Công khai',
          items: pubs,
          isPublicSection: true,
        })
      }
    }

    const map = new Map<string, SkillCatalogItem[]>()
    for (const catalogItem of filtered) {
      const key = groupCatalogKey(catalogItem)
      const list = map.get(key) || []
      list.push(catalogItem)
      map.set(key, list)
    }

    const keys = Array.from(map.keys()).sort((a, b) => {
      const order = (k: string) =>
        k.startsWith('sk:')
          ? 0
          : k.startsWith('wf:')
            ? 1
            : k.startsWith('art:')
              ? 2
              : k.startsWith('doc:')
                ? 3
                : 4
      const d = order(a) - order(b)
      if (d !== 0) return d
      return a.localeCompare(b, 'vi')
    })

    for (const key of keys) {
      const list = map.get(key)!
      const sample = list[0]
      sections.push({
        key,
        title: formatGroupTitle(sample.groupPath, sample.itemType, sample.packName),
        items: list,
      })
    }

    return sections
  })()

  const renderCard = (c: SkillCatalogItem, keyPrefix: string) => {
    const unlock = unlockByCatalogId.get(c.id)
    const viewable = canViewCatalog(c)
    const courseBadge = unlock
      ? COURSE_CODE_LABEL[unlock.courseCode] || unlock.courseCode
      : null

    return (
      <div
        key={`${keyPrefix}-${c.id}`}
        className="flex flex-col border border-eh-border bg-eh-cream p-5 transition-colors hover:bg-eh-keylime/30"
      >
        <div className="mb-3 flex items-start gap-3">
          {c.itemType === 'DOCUMENT' ? (
            <DocumentTextIcon className="h-8 w-8 shrink-0 text-eh-forest-ink" />
          ) : c.itemType === 'INSTALLER' ? (
            <ArchiveBoxIcon className="h-8 w-8 shrink-0 text-eh-forest-ink" />
          ) : (
            <FolderIcon className="h-8 w-8 shrink-0 text-eh-forest-ink" />
          )}
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap gap-1.5">
              <span className="rounded-eh-pill bg-eh-mint px-2 py-0.5 text-[10px] font-medium text-eh-forest-ink">
                {catalogTypeLabel(c.itemType)}
              </span>
              <span
                className={`rounded-eh-pill px-2 py-0.5 text-[10px] font-medium ${
                  c.isPublic
                    ? 'bg-eh-sage text-eh-forest-ink'
                    : 'bg-eh-keylime text-eh-muted'
                }`}
              >
                {c.isPublic ? 'Public' : 'Private'}
              </span>
              {courseBadge && (
                <span className="rounded-eh-pill bg-eh-sage px-2 py-0.5 text-[10px] font-medium text-eh-forest-ink">
                  {courseBadge}
                </span>
              )}
            </div>
            <h2 className="eh-heading truncate text-[1.125rem]">{c.name}</h2>
            {c.description ? (
              <p className="mt-1 line-clamp-3 text-sm text-eh-muted">{c.description}</p>
            ) : (
              <p className="mt-1 text-sm italic text-eh-muted">Chưa có mô tả</p>
            )}
          </div>
        </div>
        <div className="mt-auto text-eh-caption">
          Cập nhật: {formatSkillDate(c.updatedAt)}
          {isFileCatalogType(c.itemType) && c.rootNodeType === 'FILE' && c.rootSizeBytes != null && (
            <> · {formatFileSize(c.rootSizeBytes)}</>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          {viewable ? (
            <Link
              to={catalogViewPath(c)}
              className="eh-btn flex-1 !min-h-[40px] !px-3 !py-2 text-center text-sm"
            >
              Xem
            </Link>
          ) : (
            <span className="flex-1 border border-eh-border bg-eh-keylime/40 px-3 py-2 text-center text-sm text-eh-muted">
              Chỉ xem mô tả
            </span>
          )}
          {viewable && isFileCatalogType(c.itemType) && (
            <button
              type="button"
              disabled={downloading}
              onClick={() => handleQuickDownload(c)}
              className="inline-flex items-center justify-center gap-1 border border-eh-border px-3 py-2 text-sm text-eh-forest-ink hover:bg-eh-keylime disabled:opacity-50"
              title={isAuthenticated ? 'Tải về' : 'Đăng nhập để tải'}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Tải về
            </button>
          )}
        </div>
      </div>
    )
  }

  // Detail view
  if (numericId) {
    const unlockBadge = item && !item.isPublic && activeCourseId
      ? COURSE_CODE_LABEL[unlockByCatalogId.get(item.id)?.courseCode || ''] ||
        unlockByCatalogId.get(item.id)?.courseCode
      : null

    return (
      <div className="eh-container py-8">
        <Link
          to="/skills"
          className="mb-4 inline-flex items-center gap-1 text-sm text-m2-primary hover:underline"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Quay lại thư viện
        </Link>

        {!libraryAccess?.eligible && <Ai4baEnrollBanner />}

        {loading && !item ? (
          <p className="text-m2-on-surface-variant">Đang tải...</p>
        ) : !item ? (
          <p className="text-m2-on-surface-variant">Không mở được mục này (có thể chưa công khai)</p>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-eh border border-eh-border bg-eh-keylime/45 p-5">
              <div>
                <div className="mb-1 flex flex-wrap gap-2">
                  <span className="rounded-full bg-m2-primary/15 px-2.5 py-0.5 text-xs font-medium text-m2-primary">
                    {catalogTypeLabel(item.itemType)}
                  </span>
                  <span
                    className={`rounded-eh-pill px-2.5 py-0.5 text-xs font-medium ${
                      item.isPublic
                        ? 'bg-eh-sage text-eh-forest-ink'
                        : 'bg-eh-keylime text-eh-muted'
                    }`}
                  >
                    {item.isPublic ? 'Public' : 'Private'}
                  </span>
                  {unlockBadge && (
                    <span className="rounded-eh-pill bg-eh-sage px-2.5 py-0.5 text-xs font-medium text-eh-forest-ink">
                      {unlockBadge}
                    </span>
                  )}
                  {item.slug && (
                    <span className="rounded-full bg-m2-surface-high px-2.5 py-0.5 font-mono text-xs text-m2-on-surface-variant">
                      {item.slug}
                    </span>
                  )}
                </div>
                <h1 className="eh-title-sm">{item.name}</h1>
                {item.description && (
                  <p className="mt-1 text-sm text-m2-on-surface-variant">{item.description}</p>
                )}
                {item.groupPath && (
                  <p className="mt-2 text-xs text-m2-on-surface-variant">Nhóm: {item.groupPath}</p>
                )}
              </div>
              <button
                type="button"
                disabled={downloading}
                onClick={handleDownloadRoot}
                className="inline-flex items-center gap-2 rounded-eh-sm bg-eh-forest-ink px-4 py-2 text-sm text-eh-cream hover:bg-eh-forest-shadow disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                {!isAuthenticated
                  ? 'Đăng nhập để tải'
                  : tree.length === 1 && tree[0].nodeType === 'FILE'
                    ? 'Tải về'
                    : 'Tải ZIP'}
              </button>
            </div>

            <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
              <div className="max-h-[75vh] overflow-y-auto rounded-lg border border-m2-outline-variant bg-m2-surface-container">
                <div className="border-b border-m2-outline-variant px-3 py-2 text-xs font-semibold uppercase tracking-wide text-m2-on-surface-variant">
                  Cấu trúc thư mục
                </div>
                <SkillTree
                  nodes={tree}
                  selectedId={selectedFile?.id}
                  onSelectFile={(n) => {
                    setSelectedFile(n)
                  }}
                  onDownloadFolder={handleDownloadFolder}
                />
                {selectedFile && (
                  <div className="border-t border-m2-outline-variant p-2">
                    <button
                      type="button"
                      disabled={downloading}
                      onClick={() => handleDownloadFile(selectedFile)}
                      className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-m2-outline px-2 py-1.5 text-xs hover:bg-m2-surface-high disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Tải file đang chọn
                    </button>
                  </div>
                )}
              </div>
              <SkillFileViewer node={selectedFile} courseId={activeCourseId ?? undefined} />
            </div>
          </>
        )}
      </div>
    )
  }

  // Đang resolve slug → chờ redirect
  if (slugParam) {
    return (
      <div className="eh-container py-8">
        <p className="text-m2-on-surface-variant">Đang mở skill...</p>
      </div>
    )
  }

  return (
    <div className="eh-container py-8 md:py-10">
      <div className="mb-8 border-b border-eh-border pb-6">
        <p className="eh-eyebrow">Tài nguyên</p>
        <h1 className="eh-title">Thư viện</h1>
        <p className="mt-3 text-eh-muted">
          Skill, workflow, tài liệu và file cài đặt. Mục public: xem và tải về (cần đăng nhập). Mục
          private: chỉ xem tên và mô tả — trừ khi thuộc khóa BA / AI Coding / AI4BA bạn đang học (có nút
          Xem).
        </p>
      </div>

      {!libraryAccess?.eligible && <Ai4baEnrollBanner />}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {(['ALL', 'SKILL', 'WORKFLOW', 'ARTIFACT', 'DOCUMENT', 'INSTALLER'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-eh-pill px-3 py-1.5 text-sm ${
                filter === f
                  ? 'bg-eh-forest-ink text-eh-cream'
                  : 'bg-eh-keylime text-eh-muted hover:bg-eh-sage'
              }`}
            >
              {f === 'ALL' ? 'Tất cả' : catalogTypeLabel(f)}
            </button>
          ))}
        </div>
        <div className="w-full sm:max-w-xs sm:flex-1">
          <label htmlFor="skill-search" className="sr-only">
            Tìm kiếm
          </label>
          <input
            id="skill-search"
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Tìm theo tên, mô tả, mã..."
            className="w-full rounded-eh border border-eh-border bg-eh-cream px-3 py-2.5 text-sm text-eh-charcoal placeholder:text-eh-muted focus:border-eh-forest-ink focus:outline-none focus:ring-1 focus:ring-eh-forest-ink/40"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-eh-muted">Đang tải...</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-eh-border bg-eh-keylime/30 p-12 text-center text-eh-muted">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-10">
          {groupedSections.map((section) => (
            <section key={section.key}>
              <div className="mb-4 flex items-center gap-3 border-b border-eh-border pb-2">
                <h2
                  className={`eh-heading ${
                    section.isPublicSection ? 'text-eh-forest-ink' : 'text-eh-charcoal'
                  }`}
                >
                  {section.title}
                </h2>
                <span className="rounded-eh-pill bg-eh-mint px-2 py-0.5 text-xs text-eh-muted">
                  {section.items.length}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {section.items.map((c) => renderCard(c, section.key))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

export default SkillLibraryPage
